import { useState } from "react";
import { ChevronDown, ChevronRight, MessageSquare, MessageCircle } from "lucide-react";
import { useInbox } from "../hooks/useInbox";
import { EngagementCard } from "../components/EngagementCard";
import { Badge } from "../components/ui/Badge";
import { SkeletonList } from "../components/ui/Skeleton";

const FILTERS = ["all", "comment", "dm"] as const;
type Filter = (typeof FILTERS)[number];

const filterLabel = (f: Filter) =>
  f === "all" ? "Todos" : f === "dm" ? "DMs" : "Comentários";

const Inbox = () => {
  const { data, isLoading, error } = useInbox();
  const [filter, setFilter] = useState<Filter>("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  if (isLoading) return <SkeletonList count={4} />;
  if (error) return <p className="text-sm text-red-600">Erro ao carregar inbox.</p>;

  const items = data?.items ?? [];
  const totals = items.reduce(
    (acc, g) => {
      for (const e of g.engagements) {
        if (e.engagement.type === "dm") acc.dms++;
        else acc.comments++;
        acc.total++;
      }
      return acc;
    },
    { total: 0, dms: 0, comments: 0 }
  );

  const toggle = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Inbox</h1>
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            {totals.total} pendente{totals.total === 1 ? "" : "s"} · {totals.dms} DM
            {totals.dms === 1 ? "" : "s"} · {totals.comments} coment.
          </p>
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => {
            const active = filter === f;
            return (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`text-xs px-3 h-7 rounded-full transition-colors focus-ring ${
                  active
                    ? "bg-accent text-accent-fg"
                    : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:hover:bg-zinc-700"
                }`}
              >
                {filterLabel(f)}
              </button>
            );
          })}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 p-10 text-center text-zinc-400 dark:text-zinc-500">
          Inbox vazia. Nada novo desde a última verificação.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((group, idx) => {
            const engs = group.engagements.filter((e) =>
              filter === "all" ? true : e.engagement.type === filter
            );
            if (engs.length === 0) return null;

            const key = group.post?.id ?? `orphan-${idx}`;
            const isCollapsed = collapsed.has(key);
            const dmsCount = engs.filter((e) => e.engagement.type === "dm").length;
            const commCount = engs.length - dmsCount;
            const postDate = group.post?.publishedAt
              ? new Date(group.post.publishedAt).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "short",
                })
              : null;
            const preview = group.post
              ? group.post.content.slice(0, 80)
              : "DMs sem post associado";

            return (
              <div
                key={key}
                className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggle(key)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50 focus-ring"
                  aria-expanded={!isCollapsed}
                >
                  {isCollapsed ? (
                    <ChevronRight size={16} className="text-zinc-400 shrink-0" />
                  ) : (
                    <ChevronDown size={16} className="text-zinc-500 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">
                      {preview}
                      {group.post && group.post.content.length > 80 && (
                        <span className="text-zinc-400 dark:text-zinc-500">…</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                      {postDate && <span>{postDate}</span>}
                      {commCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <MessageCircle size={12} /> {commCount}
                        </span>
                      )}
                      {dmsCount > 0 && (
                        <span className="inline-flex items-center gap-1">
                          <MessageSquare size={12} /> {dmsCount}
                        </span>
                      )}
                      <Badge variant="accent" size="sm">
                        {engs.length} pendente{engs.length === 1 ? "" : "s"}
                      </Badge>
                    </div>
                  </div>
                </button>
                {!isCollapsed && (
                  <div className="border-t border-zinc-200 dark:border-zinc-800 p-3 space-y-3 bg-zinc-50/50 dark:bg-zinc-950/30">
                    {engs.map(({ engagement, replies }) => (
                      <EngagementCard
                        key={engagement.id}
                        engagement={engagement}
                        replies={replies}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Inbox;
